import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/i18n';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * When set, the operator must type this exact string to enable the confirm
   * button. Reserved for actions that cannot be undone, like deleting a session
   * along with its message history.
   */
  typeToConfirm?: string;
}

type Resolver = (value: boolean) => void;

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(
  null,
);

/**
 * Renders "Type <value> to confirm" with the literal kept in monospace.
 *
 * The sentence is split on the interpolated placeholder rather than assembled
 * from two half-sentences, because Thai puts the value in a different position
 * and half-sentences cannot be reordered by a translator.
 */
function TypePrompt({ sentence, value }: { sentence: string; value: string }) {
  const [before, after = ''] = sentence.split(PLACEHOLDER);
  return (
    <>
      {before}
      <span className="font-mono text-ink">{value}</span>
      {after}
    </>
  );
}

/** Not a character any translation would contain. */
const PLACEHOLDER = '⁣';

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState('');
  const resolver = useRef<Resolver | null>(null);
  const { t } = useI18n();

  const confirm = useCallback((next: ConfirmOptions) => {
    setTyped('');
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  const blocked = Boolean(options?.typeToConfirm && typed !== options.typeToConfirm);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      <Dialog open={Boolean(options)} onOpenChange={(open) => !open && settle(false)}>
        <DialogContent
          title={options?.title ?? ''}
          description={options?.description}
        >
          {options?.typeToConfirm && (
            <div className="mt-3 space-y-1.5">
              <p className="text-sm text-ink-muted">
                <TypePrompt
                  sentence={t('confirm.typePrompt', { value: PLACEHOLDER })}
                  value={options.typeToConfirm}
                />
              </p>
              <Input
                autoFocus
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                aria-label={t('confirm.typeLabel')}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => settle(false)}>
              {options?.cancelLabel ?? t('action.cancel')}
            </Button>
            <Button
              variant={options?.destructive ? 'danger' : 'primary'}
              size="sm"
              disabled={blocked}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? t('action.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return context;
}
